// Event results page model (card 34022f7d, locked mockup v52): turns the
// /feis/competition/scores/:id/(public|viewer) response into one row shape
// for every tabulation mode, and holds the page's pure rules (who is "mine",
// search, placing, event day, just published). No React here - see
// EventResults.jsx / ResultBlocks.jsx.
//
// Server rows (backend helpers/scoring + scoringSlots, trimmed by
// helpers/publicResults): best first, `overallPosition`, `irish_points`,
// `scores` = one entry per judge (1- and 3-judge competitions:
// { judge_details, score, position, irish_points, scores_by_round,
// rank_by_round, comments }) or one entry per judge SLOT (6/9-judge
// competitions: { slot_index, round, judge_fname, judge_lname, judge_photo,
// score, position, irish_points, comments }). Comments arrive only on the
// viewer's own dancers' rows.

import { formatDate } from "../../helper/formatDate";

// 1st, 2nd, 3rd, 4th ... 11th, 12th, 13th, 21st
export const ord = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 1) return "";
  const t = v % 100;
  if (t >= 11 && t <= 13) return `${v}th`;
  return `${v}${{ 1: "st", 2: "nd", 3: "rd" }[v % 10] || "th"}`;
};

// Scores exactly as entered, never rounded (75, 85.5, 70.25); sums lose only
// binary float noise (154.89999999999998 -> 154.9).
export const fmt = (x) => {
  if (x === null || x === undefined || x === "") return "";
  const v = Number(x);
  return Number.isFinite(v) ? String(Number(v.toPrecision(12))) : String(x);
};

const fold = (v) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Case- and accent-insensitive "contains" (Montréal ~ montreal).
export const includesFolded = (haystack, needle) => {
  const q = fold(needle).trim();
  return !q || fold(haystack).includes(q);
};

const judgeNameOf = (jd) => {
  if (!jd) return "";
  if (jd.name && String(jd.name).trim()) return String(jd.name).trim();
  return `${jd.fname || ""} ${jd.lname || ""}`.trim();
};

// Did the dancer actually dance? Slot tabulation pads the roster with
// position-0 placeholders; legacy rows only carry recorded scores. Same rule
// as the server's helpers/publicResults.js `danced`.
export const danced = (row) =>
  Array.isArray(row?.scores) &&
  row.scores.length > 0 &&
  row.scores.some((s) => s?.slot_index == null || Number(s.position) >= 1);

const isSlotRow = (row) => (row?.scores || []).some((s) => s?.slot_index != null);

const cardOf = (row) => {
  const c = row?.card_number;
  if (c && typeof c === "object") return c.card_number ?? null;
  return c ?? null;
};

// mine: the signed-in adjudicator's own entry (the /judge read marks it)
const legacyJudge = (s) => ({
  key: String(s?.judge_id || judgeNameOf(s?.judge_details)),
  mine: !!s?.mine,
  name: judgeNameOf(s?.judge_details),
  photo: s?.judge_details?.photo || "",
  place: Number(s?.position) || null,
  points: s?.irish_points,
  score: s?.score,
  comment: s?.comments || "",
  rounds: (s?.scores_by_round || []).map((sr, k) => ({
    round: Number(sr?.round) || k + 1,
    score: sr?.score,
    place: s?.rank_by_round?.[k]?.rank ?? null,
    comment: sr?.comments || sr?.comment || s?.rank_by_round?.[k]?.comment || "",
  })),
});

const slotJudge = (s) => ({
  key: `slot-${s.slot_index}`,
  mine: !!s.mine,
  name: `${s.judge_fname || ""} ${s.judge_lname || ""}`.trim(),
  photo: s.judge_photo || "",
  round: Number(s.round) || null,
  place: Number(s.position) || null,
  points: s.irish_points,
  score: s.score,
  comment: s.comments || "",
  rounds: [{ round: Number(s.round) || 1, score: s.score, place: Number(s.position) || null, comment: s.comments || "" }],
});

/**
 * @param {object} comp      competition list entry ({ adjudicators, rounds, ... })
 * @param {object} response  /public or /viewer response
 * @returns {{ rows, placerCount, fullField, multi, slot, rounds, judges, publishedAt }}
 *   rows: best first; each { dancerId, name, school, card, place, points,
 *   score, placed, judges: [{ key, name, photo, place, points, score,
 *   comment, rounds: [{ round, score, place, comment }] }] }
 *   judges: the column headers ({ key, name, photo, round? }) in order.
 */
export const normalizeResults = (comp, response) => {
  const raw = (response?.results || []).filter(danced);
  // The number of places is the server's (decided once, at publication);
  // this page never recalculates it. Without one, every row sent is shown
  // as sent (the server already trimmed to placers + own dancers).
  const placerCount = Number.isFinite(response?.placerCount) ? response.placerCount : raw.length;
  const slot = raw.some(isSlotRow);
  const multi = slot || Number(comp?.adjudicators) > 1 || (raw[0]?.scores?.length || 0) > 1;
  const rows = raw.map((r, i) => {
    const judges = slot
      ? [...(r.scores || [])].sort((a, b) => a.slot_index - b.slot_index).map(slotJudge)
      : (r.scores || []).map(legacyJudge);
    return {
      dancerId: String(r.dancer_id),
      name: `${r.dancer_fname || ""} ${r.dancer_lname || ""}`.trim(),
      school: r.school || "",
      card: cardOf(r),
      place: Number(r.overallPosition) || i + 1,
      points: r.irish_points,
      score: multi ? null : r.scores?.[0]?.score,
      placed: i < placerCount,
      judges,
    };
  });
  // A judge's own result (/judge): their own column(s) first, so the
  // highlighted column is in view, and every row's cells mapped to those
  // columns by judge (a dancer they didn't mark gets an empty cell there,
  // never the next judge's marks).
  // The columns are every judge seen in any row (first seen first), so a
  // judge who missed one dancer keeps their column.
  let first = rows[0]?.judges || [];
  if (response?.judge && rows.length) {
    const seen = new Map();
    rows.forEach((r) => r.judges.forEach((j) => { if (!seen.has(j.key)) seen.set(j.key, j); }));
    const all = [...seen.values()];
    const order = [...all.filter((j) => j.mine), ...all.filter((j) => !j.mine)];
    rows.forEach((r) => {
      const byKey = new Map(r.judges.map((j) => [j.key, j]));
      r.judges = order.map((j) => byKey.get(j.key) || null);
    });
    first = order;
  }
  const judges = first.map((j) => ({ key: j.key, name: j.name, photo: j.photo, round: j.round || null, mine: j.mine }));
  const rounds = slot ? 1 : Math.max(1, Number(comp?.rounds) || first[0]?.rounds?.length || 1);
  return {
    rows,
    placerCount: Math.min(placerCount, rows.length),
    fullField: !!response?.fullField,
    multi,
    slot,
    rounds,
    judges,
    publishedAt: response?.publishedAt || null,
    judge: !!response?.judge,
  };
};

// Rows a table shows: every row the server sent (placers, plus the viewer's
// own dancers, or the full field for a teacher), with a "Did not place"
// divider before the first unplaced row.
export const tableRows = (result) => {
  const rows = result?.rows || [];
  const cut = rows.findIndex((r) => !r.placed);
  return { rows, cutAt: cut === -1 ? null : cut };
};

// A results cache entry the page can show: normalized rows, not the
// "error" sentinel of a failed load (or nothing yet).
export const RESULT_ERROR = "error";
export const isLoaded = (result) => !!result && result !== RESULT_ERROR && Array.isArray(result.rows);

// Where one dancer finished. "error": the result could not be loaded, or the
// signed-in (viewer) read failed and only the public placers are known - a
// dancer missing from those rows is unknown, not "no result".
export const standingOf = (result, dancerId) => {
  if (result === RESULT_ERROR) return { state: "error" };
  if (!isLoaded(result)) return { state: "loading" };
  const row = result.rows.find((r) => r.dancerId === String(dancerId));
  if (!row) return { state: result.partial ? "error" : "none" };
  return { state: row.placed ? "placed" : "unplaced", place: row.place, row };
};

// The "Your dancers" / "Your students" list: each registered dancer with the
// competitions they are entered in (the competition roster, dancers[]).
export const dancersWithEntries = (dancers, comps) =>
  (dancers || []).map((d) => ({
    id: String(d._id),
    name: `${d.fname || ""} ${d.lname || ""}`.trim(),
    comps: (comps || []).filter((c) => (c.dancers || []).map(String).includes(String(d._id))),
  })).filter((d) => d.comps.length);

// Competition title line: "B101 · Reel" and "Under 9 · Beginner".
export const compTitle = (comp) => {
  const desc = String(comp?.description || comp?.name || "").trim();
  return desc ? desc.charAt(0).toUpperCase() + desc.slice(1).toLowerCase() : "";
};
export const compSub = (comp) => {
  const ages = (comp?.ages || []).filter(Boolean).join(", ");
  const level = String(comp?.group || "").trim();
  const levelNice = level ? level.charAt(0).toUpperCase() + level.slice(1).toLowerCase() : "";
  const champ = Number(comp?.adjudicators) > 1 ? "Championship" : "";
  return [ages, levelNice, champ].filter(Boolean).join(" · ");
};

// Search: competition code/name/level/age, plus the names, schools and card
// numbers in its loaded result, plus the viewer's own dancers entered in it.
export const compMatches = (comp, query, result, mineNames = []) => {
  const q = String(query || "").trim().replace(/^#/, "");
  if (!q) return true;
  const hay = [
    comp?.code, comp?.description, comp?.group, (comp?.ages || []).join(" "),
    ...mineNames,
    ...((result?.rows || []).flatMap((r) => [r.name, r.school, r.card != null ? String(r.card) : ""])),
  ].join(" \u0001 ");
  return includesFolded(hay, q);
};

// Level chips: the levels present, in the registration level order.
const LEVEL_ORDER = ["FIRST FEIS", "BEGINNER", "BEGINNER 1", "ADVANCED BEGINNER", "BEGINNER 2", "NOVICE", "PRIZEWINNER",
  "PRELIMINARY CHAMPIONSHIPS", "OPEN CHAMPIONSHIPS", "ADULT", "TEAMS", "SPECIALS", "NON-LEVEL", "ARTS AND CULTURE"];
const levelRank = (g) => {
  const i = LEVEL_ORDER.indexOf(String(g || "").toUpperCase());
  return i === -1 ? 999 : i;
};
export const levelsOf = (comps) =>
  [...new Set((comps || []).map((c) => c.group).filter(Boolean))].sort((a, b) => levelRank(a) - levelRank(b) || String(a).localeCompare(String(b)));

// Is today the event day (the feis date is a UTC-midnight calendar label)?
export const isEventDay = (event, now = new Date()) => {
  const d = event?.feis_date || event?.exam_date;
  if (!d) return false;
  const day = formatDate(d);
  return day.getFullYear() === now.getFullYear() && day.getMonth() === now.getMonth() && day.getDate() === now.getDate();
};

// "Just published": published during this visit (seen unpublished, then
// published on a refresh), or published in the last 15 minutes by the
// server's publishedAt.
export const JUST_PUBLISHED_MS = 15 * 60 * 1000;
export const isJustPublished = (compId, { flipped, publishedAt }, now = Date.now()) => {
  if (flipped && flipped.has(String(compId))) return true;
  const t = publishedAt ? new Date(publishedAt).getTime() : NaN;
  return Number.isFinite(t) && now - t >= 0 && now - t < JUST_PUBLISHED_MS;
};

// Which view this visitor gets (mockup: spectator / parent / teacher).
export const viewFor = ({ signedIn, organizer, isTeacher, dancers }) => {
  if (!signedIn || organizer || !(dancers || []).length) return "spectator";
  return isTeacher ? "teacher" : "parent";
};
